import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

// All toggleable modules with metadata
export interface ToggleableModule {
  id: string;
  label: string;
  description: string;
  group: 'navigation' | 'kommunikation' | 'integration';
  icon: string;
  defaultEnabled: boolean;
}

export const TOGGLEABLE_MODULES: ToggleableModule[] = [
  // Hauptmodule - Navigation
  { id: 'projekte', label: 'Projekte', description: 'Projektverwaltung und Projektdetails', group: 'navigation', icon: 'FolderKanban', defaultEnabled: true },
  { id: 'aufgaben', label: 'Aufgaben', description: 'Aufgaben- und Ticketverwaltung', group: 'navigation', icon: 'CheckSquare', defaultEnabled: true },
  { id: 'finanzen', label: 'Finanzen', description: 'Buchungen, Einnahmen und Ausgaben', group: 'navigation', icon: 'Wallet', defaultEnabled: true },
  { id: 'versicherungen', label: 'Versicherungen', description: 'Versicherungsübersicht und -verwaltung', group: 'navigation', icon: 'Shield', defaultEnabled: true },
  { id: 'marketing', label: 'Marketing', description: 'Kampagnen, Social Media und Werbung', group: 'navigation', icon: 'Megaphone', defaultEnabled: true },
  { id: 'daten', label: 'Daten', description: 'Dateimanagement und Datenablage', group: 'navigation', icon: 'Database', defaultEnabled: true },
  { id: 'kalender', label: 'Kalender', description: 'Termine und Eventplanung', group: 'navigation', icon: 'Calendar', defaultEnabled: true },
  // Hauptmodule - Kommunikation
  { id: 'teamchat', label: 'Teamchat', description: 'Interne Kommunikation im Team', group: 'kommunikation', icon: 'MessageCircle', defaultEnabled: true },
  { id: 'email', label: 'E-Mail', description: 'E-Mail-Verwaltung', group: 'kommunikation', icon: 'Mail', defaultEnabled: true },
  { id: 'ki-assistent', label: 'KI-Assistent', description: 'KI-gestützter Assistent für Recherche', group: 'kommunikation', icon: 'Bot', defaultEnabled: true },
  { id: 'kontakte', label: 'Kontakte', description: 'CRM und Kontaktverwaltung', group: 'kommunikation', icon: 'Contact', defaultEnabled: true },
  // Integrationsmodule
  { id: 'nas_storage', label: 'NAS & Server', description: 'Externe Speichersysteme (Synology, Nextcloud, WebDAV)', group: 'integration', icon: 'Server', defaultEnabled: true },
  { id: 'integrationen', label: 'Integrationen', description: 'Drittanbieter-Integrationen und API-Verbindungen', group: 'integration', icon: 'Plug', defaultEnabled: true },
];

export const SIDEBAR_TO_MODULE_MAP: Record<string, string> = {
  'projects': 'projekte',
  'tasks': 'aufgaben',
  'finanzen': 'finanzen',
  'insurance': 'versicherungen',
  'marketing': 'marketing',
  'data': 'daten',
  'calendar': 'kalender',
  'chat': 'teamchat',
  'email': 'email',
  'ki-chat': 'ki-assistent',
  'contacts': 'kontakte',
  'storage': 'nas_storage',
  'integrations': 'integrationen',
};

const SETTING_PREFIX = 'module_visible_';
const VISIBILITY_CHANGE_EVENT = 'module-visibility-changed';

export const useModuleVisibility = () => {
  const { user } = useAuthContext();
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    TOGGLEABLE_MODULES.forEach(m => { defaults[m.id] = m.defaultEnabled; });
    return defaults;
  });
  const [loading, setLoading] = useState(true);
  const isToggling = useRef(false);

  const fetchVisibility = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .is('project_id', null)
        .like('setting_key', `${SETTING_PREFIX}%`);

      if (error) throw error;

      const visMap: Record<string, boolean> = {};
      TOGGLEABLE_MODULES.forEach(m => {
        visMap[m.id] = m.defaultEnabled;
      });

      (data || []).forEach((row: any) => {
        const moduleId = row.setting_key.replace(SETTING_PREFIX, '');
        visMap[moduleId] = row.setting_value === true || row.setting_value === 'true';
      });

      setVisibility(visMap);
    } catch (err) {
      console.error('Error fetching module visibility:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchVisibility();
  }, [fetchVisibility]);

  // Listen for changes from other hook instances
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setVisibility(prev => ({ ...prev, [detail.moduleId]: detail.enabled }));
      }
    };
    window.addEventListener(VISIBILITY_CHANGE_EVENT, handler);
    return () => window.removeEventListener(VISIBILITY_CHANGE_EVENT, handler);
  }, []);

  const toggleModule = async (moduleId: string, enabled: boolean) => {
    if (isToggling.current) return;
    isToggling.current = true;

    const key = `${SETTING_PREFIX}${moduleId}`;

    // Optimistic update locally + notify other instances
    setVisibility(prev => ({ ...prev, [moduleId]: enabled }));
    window.dispatchEvent(new CustomEvent(VISIBILITY_CHANGE_EVENT, { detail: { moduleId, enabled } }));

    try {
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .is('project_id', null)
        .eq('setting_key', key)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('system_settings')
          .update({ setting_value: enabled, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('system_settings')
          .insert({ setting_key: key, setting_value: enabled, project_id: null });
      }
    } catch (err) {
      console.error('Error toggling module:', err);
      // Revert
      setVisibility(prev => ({ ...prev, [moduleId]: !enabled }));
      window.dispatchEvent(new CustomEvent(VISIBILITY_CHANGE_EVENT, { detail: { moduleId, enabled: !enabled } }));
    } finally {
      isToggling.current = false;
    }
  };

  const isModuleVisible = (sidebarItemId: string): boolean => {
    const moduleId = SIDEBAR_TO_MODULE_MAP[sidebarItemId];
    if (!moduleId) return true;
    return visibility[moduleId] ?? true;
  };

  return {
    visibility,
    loading,
    toggleModule,
    isModuleVisible,
    modules: TOGGLEABLE_MODULES,
  };
};
